/**
 * <sun-predictive-search>
 *
 * Predictive search Web Component for the Sun Society full-page menu overlay.
 *
 * SSR-first: the empty state (curated collections + products) is rendered by
 * snippets/sun-predictive-search.liquid at page load. This component only
 * orchestrates state and, on input, fetches the Predictive Search API and
 * swaps <sun-predictive-search--results> inner HTML.
 *
 * State machine (data-attributes on the custom element root):
 *   - data-state="closed|open"  — panel visibility (closed = hidden)
 *   - .has-query                — input has a non-empty value (hides placeholder
 *                                 instantly, before the debounced fetch runs)
 *   - .has-results              — swap placeholder for active-state results
 *   - .is-loading               — inflight fetch (for spinner / style hook)
 *
 * The overlay root also gets data-search-open="true|false" so CSS can react
 * (animate the < RETOUR button, shrink the input) from the topbar selector.
 *
 * API:
 *   open(), close(), clear() — public methods (useful for custom events).
 *
 * Depends on:
 *   - <sun-menu-overlay> ancestor
 *   - an input.sun-menu-overlay__search__input inside the overlay
 *   - a [data-sun-search-back] button in the topbar (rendered in #36)
 */
(function () {
  if (customElements.get('sun-predictive-search')) return;

  const DEBOUNCE_MS = 300;

  function debounce(fn, wait) {
    let timeoutId;
    return function debounced(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function normalizeQueryKey(query) {
    return query.toLowerCase().replace(/\s+/g, '-');
  }

  class SunPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cache = Object.create(null);
      this.abortController = null;

      this.onFocus = this.onFocus.bind(this);
      // Instant handler toggles .has-query on every keystroke — lets the CSS
      // hide the placeholder before the debounced fetch runs.
      this.onInputImmediate = this.onInputImmediate.bind(this);
      this.onInput = debounce(this.onInput.bind(this), DEBOUNCE_MS);
      this.onBack = this.onBack.bind(this);
      this.onOverlayClose = this.onOverlayClose.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.overlay = this.closest('sun-menu-overlay');
      this.input = this.overlay && this.overlay.querySelector('.sun-menu-overlay__search__input');
      this.backButton = this.overlay && this.overlay.querySelector('[data-sun-search-back]');
      this.resultsContainer = this.querySelector('sun-predictive-search--results');
      this.placeholder = this.querySelector('[data-sun-search-placeholder]');

      this.sectionId = this.dataset.sectionId || 'sun-predictive-search';
      this.resourceTypes = this.dataset.resourceTypes || '';
      this.resourceLimit = this.dataset.resourceLimit || '';
      this.resourceLimitScope = this.dataset.resourceLimitScope || '';

      if (!this.input || !this.resultsContainer) return;

      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('input', this.onInputImmediate);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);

      if (this.backButton) {
        this.backButton.addEventListener('click', this.onBack);
      }

      if (this.overlay) {
        this.overlay.addEventListener('sun-menu:close', this.onOverlayClose);
      }
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('focus', this.onFocus);
        this.input.removeEventListener('input', this.onInputImmediate);
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('keydown', this.onKeydown);
      }
      if (this.backButton) {
        this.backButton.removeEventListener('click', this.onBack);
      }
      if (this.overlay) {
        this.overlay.removeEventListener('sun-menu:close', this.onOverlayClose);
      }
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    open() {
      this.setAttribute('data-state', 'open');
      if (this.overlay) this.overlay.setAttribute('data-search-open', 'true');
      if (this.input) this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.setAttribute('data-state', 'closed');
      if (this.overlay) this.overlay.setAttribute('data-search-open', 'false');
      if (this.input) this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('has-query', 'has-results', 'is-loading');
    }

    clear() {
      if (this.input) this.input.value = '';
      if (this.resultsContainer) this.resultsContainer.innerHTML = '';
      this.classList.remove('has-query', 'has-results');
    }

    onFocus() {
      this.open();
    }

    onBack() {
      this.clear();
      this.close();
      if (this.input) this.input.blur();
    }

    onOverlayClose() {
      this.clear();
      this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.getAttribute('data-state') !== 'open') return;
      event.stopPropagation();
      this.onBack();
    }

    // Fires on every keystroke (not debounced) — only toggles the
    // `has-query` class so the placeholder hides immediately while the user
    // is still typing. The actual fetch is handled by the debounced onInput.
    onInputImmediate() {
      const hasValue = (this.input.value || '').trim().length > 0;
      this.classList.toggle('has-query', hasValue);
      if (!hasValue) {
        this.classList.remove('has-results', 'is-loading');
      }
    }

    onInput() {
      const query = (this.input.value || '').trim();
      if (query.length === 0) {
        this.classList.remove('has-query', 'has-results', 'is-loading');
        if (this.resultsContainer) this.resultsContainer.innerHTML = '';
        return;
      }
      this.getSearchResults(query);
    }

    getSearchResults(query) {
      const key = normalizeQueryKey(query);

      if (this.cache[key] !== undefined) {
        this.renderResults(this.cache[key]);
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      const controller = this.abortController;

      const params = new URLSearchParams();
      params.set('q', query);
      params.set('section_id', this.sectionId);
      if (this.resourceTypes) params.set('resources[type]', this.resourceTypes);
      if (this.resourceLimit) params.set('resources[limit]', this.resourceLimit);
      if (this.resourceLimitScope) params.set('resources[limit_scope]', this.resourceLimitScope);

      const base =
        (window.theme && window.theme.routes && window.theme.routes.predictive_search_url) ||
        '/search/suggest';
      const url = `${base}?${params.toString()}`;

      this.classList.add('is-loading');

      fetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const results = doc.querySelector('sun-predictive-search--results');
          const inner = results ? results.innerHTML : '';
          this.cache[key] = inner;
          this.renderResults(inner);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          console.error('[sun-predictive-search]', err);
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            this.classList.remove('is-loading');
          }
          if (this.abortController === controller) {
            this.abortController = null;
          }
        });
    }

    renderResults(innerHTML) {
      if (!this.resultsContainer) return;
      this.resultsContainer.innerHTML = innerHTML;
      this.classList.add('has-results');
    }
  }

  customElements.define('sun-predictive-search', SunPredictiveSearch);
})();
